// src/utils/searchParser.js
// Parses natural language search queries into structured filters

import { 
  SPORTS_OPTIONS, 
  AGE_GROUP_OPTIONS
} from '../constants/exploreFilters';

/**
 * Parse a natural language search query into filter values
 * @param {string} query - The user's search query
 * @returns {Object} Parsed filters and remaining search text
 */
export const parseSearchQuery = (query) => {
  if (!query || typeof query !== 'string') {
    return { filters: {}, remainingQuery: '' };
  }

  let workingQuery = query.toLowerCase().trim();
  const filters = {};

  // Parse sport
  const sportMatch = parseSport(workingQuery);
  if (sportMatch.sport) {
    filters.sport = sportMatch.sport;
    workingQuery = sportMatch.remaining;
  }

  // Parse age
  const ageMatch = parseAge(workingQuery);
  if (ageMatch.ageGroup) {
    filters.ageGroup = ageMatch.ageGroup;
    workingQuery = ageMatch.remaining;
  }

  // Parse price
  const priceMatch = parsePrice(workingQuery);
  if (priceMatch.priceRange) {
    filters.priceRange = priceMatch.priceRange;
    workingQuery = priceMatch.remaining;
  }

  // Parse start date
  const dateMatch = parseStartDate(workingQuery);
  if (dateMatch.startDate) {
    filters.startDate = dateMatch.startDate;
    workingQuery = dateMatch.remaining;
  }

  // Parse availability
  const availabilityMatch = parseAvailability(workingQuery);
  if (availabilityMatch.hasOpenings !== undefined) {
    filters.hasOpenings = availabilityMatch.hasOpenings;
    workingQuery = availabilityMatch.remaining;
  }

  // Clean up remaining query
  const remainingQuery = cleanupQuery(workingQuery);

  return { filters, remainingQuery };
};

/**
 * Parse sport from query
 */
const parseSport = (query) => {
  const sportKeywords = {
    'soccer': 'Soccer',
    'football': 'Football',
    'basketball': 'Basketball',
    'baseball': 'Baseball',
    'tennis': 'Tennis',
    'volleyball': 'Volleyball',
    'lacrosse': 'Lacrosse',
    'hockey': 'Hockey',
    'swimming': 'Swimming',
    'swim': 'Swimming',
    'golf': 'Golf',
    'track': 'Track',
    'track and field': 'Track',
    'gymnastics': 'Gymnastics',
    'martial arts': 'Martial Arts',
    'karate': 'Martial Arts',
    'taekwondo': 'Martial Arts',
    'judo': 'Martial Arts',
    'wrestling': 'Wrestling',
    'cricket': 'Cricket',
    'rugby': 'Rugby',
    'badminton': 'Badminton',
    'table tennis': 'Table Tennis',
    'ping pong': 'Table Tennis'
  };

  for (const [keyword, sportValue] of Object.entries(sportKeywords)) {
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    if (regex.test(query)) {
      return {
        sport: sportValue,
        remaining: query.replace(regex, '').trim()
      };
    }
  }

  return { sport: null, remaining: query };
};

/**
 * Parse age from query
 */
const parseAge = (query) => {
  // Age patterns to match
  const agePatterns = [
    // "for 8 year olds", "8 years old", "age 8"
    { regex: /\b(?:for\s+)?(?:age[s]?\s+)?(\d{1,2})\s*(?:year[s]?\s*old[s]?|yr[s]?|y\.?o\.?)\b/i, type: 'single' },
    // "ages 8-10", "8-10 years", "age 8 to 10"
    { regex: /\b(?:age[s]?\s+)?(\d{1,2})\s*[-–to]+\s*(\d{1,2})(?:\s*(?:year[s]?|yr[s]?))?\b/i, type: 'range' },
    // "under 8", "below 10"
    { regex: /\b(?:under|below|younger than|less than)\s+(\d{1,2})\b/i, type: 'under' },
    // "over 18", "above 16", "18+"
    { regex: /\b(?:over|above|older than|more than)\s+(\d{1,2})\b/i, type: 'over' },
    { regex: /\b(\d{1,2})\+\b/i, type: 'over' },
    // "toddler", "kids", "youth", "teen", "adult"
    { regex: /\btoddler[s]?\b/i, type: 'keyword', value: 'toddler' },
    { regex: /\b(?:kid[s]?|children|child)\b/i, type: 'keyword', value: 'u12' },
    { regex: /\byouth\b/i, type: 'keyword', value: 'u14' },
    { regex: /\bteen[s]?(?:ager[s]?)?\b/i, type: 'keyword', value: 'u18' },
    { regex: /\badult[s]?\b/i, type: 'keyword', value: 'adult' },
    { regex: /\bsenior[s]?\b/i, type: 'keyword', value: 'senior' },
    // U-groups: "u8", "u10", "u12"
    { regex: /\bu[-]?(\d{1,2})\b/i, type: 'u-group' }
  ];

  for (const pattern of agePatterns) {
    const match = query.match(pattern.regex);
    if (match) {
      let ageGroup = null;

      if (pattern.type === 'keyword') {
        ageGroup = pattern.value;
      } else if (pattern.type === 'single') {
        const age = parseInt(match[1], 10);
        ageGroup = ageFromSingle(age);
      } else if (pattern.type === 'range') {
        const minAge = parseInt(match[1], 10);
        const maxAge = parseInt(match[2], 10);
        ageGroup = ageFromRange(minAge, maxAge);
      } else if (pattern.type === 'under') {
        const age = parseInt(match[1], 10);
        ageGroup = ageFromUnder(age);
      } else if (pattern.type === 'over') {
        const age = parseInt(match[1], 10);
        ageGroup = ageFromOver(age);
      } else if (pattern.type === 'u-group') {
        const age = parseInt(match[1], 10);
        ageGroup = `u${age}`;
        // Validate it exists
        if (!AGE_GROUP_OPTIONS.find(o => o.value === ageGroup)) {
          ageGroup = ageFromSingle(age);
        }
      }

      if (ageGroup) {
        return {
          ageGroup,
          remaining: query.replace(match[0], '').trim()
        };
      }
    }
  }

  return { ageGroup: null, remaining: query };
};

// Helper functions for age parsing
const ageFromSingle = (age) => {
  if (age <= 4) return 'toddler';
  if (age <= 6) return 'u6';
  if (age <= 8) return 'u8';
  if (age <= 10) return 'u10';
  if (age <= 12) return 'u12';
  if (age <= 14) return 'u14';
  if (age <= 16) return 'u16';
  if (age <= 18) return 'u18';
  if (age <= 50) return 'adult';
  return 'senior';
};

const ageFromRange = (min, max) => {
  const avg = (min + max) / 2;
  return ageFromSingle(Math.round(avg));
};

const ageFromUnder = (age) => {
  if (age <= 6) return 'toddler';
  if (age <= 8) return 'u6';
  if (age <= 10) return 'u8';
  if (age <= 12) return 'u10';
  if (age <= 14) return 'u12';
  if (age <= 16) return 'u14';
  if (age <= 18) return 'u16';
  return 'u18';
};

const ageFromOver = (age) => {
  if (age >= 50) return 'senior';
  if (age >= 18) return 'adult';
  if (age >= 16) return 'u18';
  if (age >= 14) return 'u16';
  if (age >= 12) return 'u14';
  if (age >= 10) return 'u12';
  return 'u10';
};

/**
 * Parse price from query
 */
const parsePrice = (query) => {
  const pricePatterns = [
    // "free"
    { regex: /\bfree\b/i, value: 'free' },
    // "under $50", "less than $100", "below 200"
    { regex: /\b(?:under|below|less than|cheaper than|max)\s*\$?\s*(\d+)\b/i, type: 'under' },
    // "$50-$100", "$100 to $200"
    { regex: /\$?\s*(\d+)\s*[-–to]+\s*\$?\s*(\d+)\b/i, type: 'range' },
    // "around $100", "about $50"
    { regex: /\b(?:around|about|approximately|~)\s*\$?\s*(\d+)\b/i, type: 'around' },
    // "cheap", "affordable", "budget"
    { regex: /\b(?:cheap|affordable|budget|inexpensive)\b/i, value: 'under50' },
    // "expensive", "premium"
    { regex: /\b(?:expensive|premium|high[- ]?end)\b/i, value: 'over500' }
  ];

  for (const pattern of pricePatterns) {
    const match = query.match(pattern.regex);
    if (match) {
      let priceRange = null;

      if (pattern.value) {
        priceRange = pattern.value;
      } else if (pattern.type === 'under') {
        const amount = parseInt(match[1], 10);
        priceRange = priceFromUnder(amount);
      } else if (pattern.type === 'range') {
        const max = parseInt(match[2], 10);
        priceRange = priceFromUnder(max);
      } else if (pattern.type === 'around') {
        const amount = parseInt(match[1], 10);
        priceRange = priceFromAround(amount);
      }

      if (priceRange) {
        return {
          priceRange,
          remaining: query.replace(match[0], '').trim()
        };
      }
    }
  }

  return { priceRange: null, remaining: query };
};

const priceFromUnder = (amount) => {
  if (amount <= 0) return 'free';
  if (amount <= 50) return 'under50';
  if (amount <= 100) return '50to100';
  if (amount <= 200) return '100to200';
  if (amount <= 500) return '200to500';
  return 'over500';
};

const priceFromAround = (amount) => {
  if (amount <= 25) return 'under50';
  if (amount <= 75) return '50to100';
  if (amount <= 150) return '100to200';
  if (amount <= 350) return '200to500';
  return 'over500';
};

/**
 * Parse start date from query
 */
const parseStartDate = (query) => {
  const datePatterns = [
    { regex: /\b(?:this month|starting this month|begins this month)\b/i, value: 'thisMonth' },
    { regex: /\b(?:next month|starting next month|begins next month)\b/i, value: 'nextMonth' },
    { regex: /\b(?:next 3 months|within 3 months|next three months)\b/i, value: 'next3Months' },
    { regex: /\b(?:spring|in spring|this spring)\b/i, value: 'spring' },
    { regex: /\b(?:summer|in summer|this summer)\b/i, value: 'summer' },
    { regex: /\b(?:fall|autumn|in fall|this fall)\b/i, value: 'fall' },
    { regex: /\b(?:starting soon|begins soon|upcoming)\b/i, value: 'thisMonth' },
    { regex: /\b(?:later|future|anytime)\b/i, value: 'all' }
  ];

  for (const pattern of datePatterns) {
    const match = query.match(pattern.regex);
    if (match) {
      return {
        startDate: pattern.value,
        remaining: query.replace(match[0], '').trim()
      };
    }
  }

  return { startDate: null, remaining: query };
};

/**
 * Parse availability from query
 */
const parseAvailability = (query) => {
  const availabilityPatterns = [
    { regex: /\b(?:available|has openings|spots available|open spots|not full)\b/i, value: true },
    { regex: /\b(?:with availability|with spots|accepting)\b/i, value: true },
    { regex: /\b(?:waitlist|full)\b/i, value: false }
  ];

  for (const pattern of availabilityPatterns) {
    const match = query.match(pattern.regex);
    if (match) {
      return {
        hasOpenings: pattern.value,
        remaining: query.replace(match[0], '').trim()
      };
    }
  }

  return { hasOpenings: undefined, remaining: query };
};

/**
 * Clean up remaining query text
 */
const cleanupQuery = (query) => {
  // Remove common filler words
  const fillerWords = ['for', 'with', 'the', 'a', 'an', 'in', 'at', 'and', 'or', 'of', 'to', 'programs', 'program', 'classes', 'class', 'clubs', 'club', 'lessons', 'lesson'];
  
  let cleaned = query;
  fillerWords.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    cleaned = cleaned.replace(regex, '');
  });

  // Remove extra whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
};

/**
 * Get filter suggestions based on partial input
 * @param {string} input - Partial user input
 * @returns {Array} Suggested completions
 */
export const getSearchSuggestions = (input) => {
  if (!input || input.length < 2) return [];

  const suggestions = [];
  const lowerInput = input.toLowerCase();

  // Sport suggestions
  SPORTS_OPTIONS.forEach(sport => {
    if (sport.value !== 'All' && sport.label.toLowerCase().includes(lowerInput)) {
      suggestions.push({
        type: 'sport',
        text: sport.label,
        display: `🏃 ${sport.label}`,
        filter: { sport: sport.value }
      });
    }
  });

  // Age suggestions
  const ageKeywords = [
    { keyword: 'kids', label: 'Kids (Under 12)', value: 'u12' },
    { keyword: 'toddler', label: 'Toddler (2-4)', value: 'toddler' },
    { keyword: 'youth', label: 'Youth (13-14)', value: 'u14' },
    { keyword: 'teen', label: 'Teen (15-18)', value: 'u18' },
    { keyword: 'adult', label: 'Adult (18+)', value: 'adult' },
    { keyword: 'senior', label: 'Senior (50+)', value: 'senior' }
  ];

  ageKeywords.forEach(age => {
    if (age.keyword.includes(lowerInput) || age.label.toLowerCase().includes(lowerInput)) {
      suggestions.push({
        type: 'age',
        text: age.label,
        display: `👶 ${age.label}`,
        filter: { ageGroup: age.value }
      });
    }
  });

  // Price suggestions
  if ('free'.includes(lowerInput) || 'cheap'.includes(lowerInput)) {
    suggestions.push({
      type: 'price',
      text: 'Free programs',
      display: '💰 Free programs',
      filter: { priceRange: 'free' }
    });
  }

  // Time suggestions
  const timeKeywords = [
    { keyword: 'summer', display: '☀️ Summer programs', value: 'summer' },
    { keyword: 'spring', display: '🌸 Spring programs', value: 'spring' },
    { keyword: 'fall', display: '🍂 Fall programs', value: 'fall' }
  ];

  timeKeywords.forEach(time => {
    if (time.keyword.includes(lowerInput)) {
      suggestions.push({
        type: 'startDate',
        text: time.display,
        display: time.display,
        filter: { startDate: time.value }
      });
    }
  });

  return suggestions.slice(0, 5);
};

export default parseSearchQuery;










